import React, { useId, useState } from 'react';
import { Button, Input, type InputRef } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import css from './ResourceEditor.scss';

const focusName = (input: InputRef | null) => {
	if (input?.input && input.input !== document.activeElement) {
		input.focus({ preventScroll: true, cursor: 'all' });
	}
};

export default function ResourceName({
	name,
	label,
	title = name,
	disabled,
	validateName,
	onRename,
}: {
	name: string;
	label: string;
	title?: string;
	disabled?: boolean;
	validateName?: (name: string) => string | undefined;
	onRename: (name: string) => void;
}) {
	const [draft, setDraft] = useState<string>();
	const errorId = useId();
	const validate = (value: string) => {
		if (!value.trim()) return `Enter a ${label.toLowerCase()} name.`;
		return validateName?.(value.trim());
	};
	const error = draft === undefined ? undefined : validate(draft);
	const save = () => {
		if (draft === undefined || error || disabled) return;
		const next = draft.trim();
		setDraft(undefined);
		if (next !== name) onRename(next);
	};
	return draft === undefined ? (
		<span className={css.editableName}>
			<span title={title}>{name}</span>
			<Button
				type="text"
				size="small"
				className={css.nameAction}
				data-resource-name-action
				icon={<EditOutlined />}
				aria-label={`Rename ${label.toLowerCase()} ${title}`}
				disabled={disabled}
				onClick={event => {
					event.stopPropagation();
					setDraft(name);
				}}
				onKeyDown={event => event.stopPropagation()}
			/>
		</span>
	) : (
		<span className={css.nameField}>
			<Input
				ref={focusName}
				size="small"
				aria-label={`${label} name: ${title}`}
				aria-invalid={Boolean(error)}
				aria-describedby={error ? errorId : undefined}
				status={error ? 'error' : undefined}
				value={draft}
				disabled={disabled}
				onChange={event => setDraft(event.target.value)}
				onClick={event => event.stopPropagation()}
				onBlur={save}
				onKeyDown={event => {
					event.stopPropagation();
					if (event.key === 'Enter') {
						event.preventDefault();
						save();
					} else if (event.key === 'Escape') {
						event.preventDefault();
						setDraft(undefined);
					}
				}}
			/>
			{error && <span id={errorId}>{error}</span>}
		</span>
	);
}
